/**
 * ⚠️ **THE DOCUMENT CUSTODY VERB — granted by step 1, ARMED by step 2 (steward-app#742).**
 *
 * appcore's `custodyVerb` (`packages/core/src/entities/custom-type.ts`) is the ELEVATED
 * tier of a type's own ACL. When a type declares one, two operations stop being satisfied
 * by the read tier and require an ACL entry carrying THIS verb instead:
 *
 *   (a) DESTROYING an instance, and
 *   (b) CHANGING its access parent in either direction, including detaching it to a root.
 *
 * Everything else — list, get, download, ordinary metadata edit — stays at `readVerb`.
 * That is exactly the pair steward-app#742 needs closed before an advisor is ever given
 * `inst:write`: without it, a writer who can see a folder can delete the family's
 * documents out of it, or re-file them to an unrestricted root and read them there.
 *
 * ## Why this string is written onto ACL entries BEFORE anything declares it
 *
 * appcore matches per-verb against a per-target opt-in. A folder with entries is
 * restricted; an entry is checked with `verbs.includes(verb)` for the ONE verb being
 * asked about. So the moment `document-folder` declares a `custodyVerb`, every EXISTING
 * entry that does not carry it stops satisfying (a) and (b) — the family would lose
 * delete and move inside their own shared folder, which prod has one live example of.
 * appcore's own docblock states the hazard ("introducing a NEW verb silently DENIES every
 * existing grantee whose entry predates it") and appcore#553 §18.5 "Before arming it"
 * records it as the ordering rule. Hence the split: this PR makes every family ROLE entry
 * this app writes carry the verb, and a LATER PR declares it, once the inventory says no
 * reachable family entry is missing it.
 *
 * ## Why `custody` and not `reveal`
 *
 * `credential` and `credential-vault` declare `custodyVerb: 'reveal'`
 * (`credentialAccessControl.test.ts` pins both). That was the right choice THERE for one
 * reason that does not transfer: `reveal` was an EXISTING disclosure tier those grantees
 * already held, so arming it re-granted nobody. No document-folder entry has ever carried
 * `reveal` — every entry ever written by this app carries `read` plus a provenance marker
 * — so reusing it would be exactly as new as any other string, while naming something a
 * folder does not have (there is no secret to reveal). appcore explicitly leaves the
 * choice to the type: "a type with no secret could just as well name its own".
 *
 * `custody` names the tier rather than an operation, which is the discipline appcore asks
 * for: one verb covering destroy AND re-home, not `delete` + `move`, because per-verb
 * matching multiplies the "entry grants a verb no gate checks" failure mode.
 *
 * ## What it does today
 *
 * ⚠️ **This section used to read "Nothing", and step 2 ended that.** `document` and
 * `document-folder` both declare `custodyVerb: DOCUMENT_CUSTODY_VERB` (`index.ts`), so
 * appcore now asks about this verb on every destroy of a document or folder and on every
 * change of either one's access parent. An entry carrying it is a LIVE grant of that tier;
 * an entry lacking it fails those two operations on a restricted folder.
 *
 * `documentCustodyVerb.test.ts` is still the ratchet, inverted: it pins that exactly the
 * two document types declare exactly this constant, so a third type acquiring it — or a
 * document type losing it — is as loud as the arming was. The arming condition itself was
 * live data, not code: a fresh `scripts/backfill-folder-custody-verb.ts` dry run reading
 * `safeToArm: true` in the target environment. That condition applies again to any
 * environment this pack has not yet been seeded into.
 */
export const DOCUMENT_CUSTODY_VERB = 'custody';

/**
 * ⚠️ **THE CONFINEMENT WRITE VERB — the string that lets a write-confined role write at
 * all (steward-app#742 step 4).**
 *
 * appcore's write confinement (crossmarc/appcore#553, docs/06 §18) says: a caller holding
 * any role with `writeConfined: true` may write ONLY where an ACL entry names them — their
 * `USR#<userId>` or one of their `ROLE#<roleId>`s — and that entry carries this verb.
 * Tier-3's opt-in fallback ("a target with zero entries is unrestricted") does not apply to
 * them on the write intent. Reads are untouched on every path.
 *
 * The literal is appcore's `CONFINEMENT_WRITE_VERB` (`packages/core/src/entities/acl.ts`,
 * verified at source 2026-09-02). It is spelled here rather than imported for the same
 * reason `ENGAGEMENT_ACTIVATE_CAPABILITY` is spelled in `packRoles.ts`: this package
 * deliberately does not depend on `@appcore/core`. A typo grants nobody anything — the gate
 * fails closed — so the failure mode is a contributor who can never write, not one who can
 * write somewhere they should not.
 *
 * ## It is the OPPOSITE of the custody verb above, and both live here on purpose
 *
 * `DOCUMENT_CUSTODY_VERB` is WITHHELD from the advisor's entry and granted to the family's;
 * this one is GRANTED to the advisor's entry, and only when the family opts in. One file so
 * that a reader deciding what an entry's `verbs` array should say sees both halves of the
 * answer at once, and so that the string this app WRITES and the string appcore MATCHES
 * cannot drift apart in two places instead of one.
 *
 * ## Grant before arm, and it applies to this verb too (§18.5 item 1)
 *
 * The entry must carry `write` BEFORE the confined role reaches the membership, or arming
 * the role is a total write outage for its holder rather than a partial one. In this app
 * that ordering is `handlers/advisor.ts`' reconcile: the advisor's ACL entry is written
 * first, the contributor role is composed onto the membership after it, and the opt-OUT
 * runs the two in the opposite order.
 *
 * ⚠️ **§18.5 item 2 is the converse and it was audited before this shipped.** Any entry
 * that already carried `write` for some consumer's own meaning becomes a confinement grant
 * the moment a confined role exists. Every folder ACL entry this app has ever written
 * carries `read` plus a provenance marker (and, since step 1, `custody` on the family half)
 * — never `write`; `scripts/backfill-folder-custody-verb.ts`' inventory is the evidence, and
 * `handlers/folderAcl.ts`' `FOLDER_VERBS` is still `['read']`, so the folder ACL manager
 * cannot mint one either.
 */
export const FOLDER_CONTRIBUTE_VERB = 'write';
